import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import DashboardLayout from '@/components/layout/DashboardLayout';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { useCategories } from '@/hooks/useCategories';
import { SubmitReviewDialog } from '@/pages/builder/ReviewControls';
import { InlineEdit } from '@/components/InlineEdit';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { toast } from 'sonner';
import {
  Plus, Search, Loader2, MoreHorizontal, Edit, Copy, Trash2, Eye, EyeOff, Send,
  BookOpen, Users,
} from 'lucide-react';


const CourseManagementPage = () => {
  const { api, user } = useAuth();
  const CATEGORIES = useCategories();
  const canDelete = user?.role === 'super_admin';
  const base = user?.role === 'super_admin' ? '/admin/courses' : '/cms/courses';
  const navigate = useNavigate();
  const [courses, setCourses] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [statusCounts, setStatusCounts] = useState(null);
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [selectedIds, setSelectedIds] = useState([]);
  const [submitCourseId, setSubmitCourseId] = useState(null);
  const [reviewFilter, setReviewFilter] = useState('all');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  useEffect(() => { const t = setTimeout(() => setDebouncedSearch(search), 300); return () => clearTimeout(t); }, [search]);

  const [loadError, setLoadError] = useState(null);

  const fetchCourses = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (statusFilter !== 'all') params.append('status', statusFilter);
      if (categoryFilter !== 'all') params.append('category', categoryFilter);
      if (debouncedSearch) params.append('search', debouncedSearch);
      params.append('limit', '200');
      const res = await api.get(`/courses/admin/list?${params}`);
      if (!Array.isArray(res.data?.courses)) throw new Error('Unexpected response from the course list');
      setCourses(res.data.courses);
      setTotal(res.data?.total || 0);
      setStatusCounts(res.data?.status_counts || null);
      setLoadError(null);
    } catch (err) {
      if (err.response?.status === 401 || err.response?.status === 403) {
        toast.error('Session expired. Please log in again.');
      } else {
        setLoadError(err.response?.status ? `The server returned error ${err.response.status}` : (err.message || 'The server could not be reached'));
      }
    }
    finally { setLoading(false); }
  }, [api, statusFilter, categoryFilter, debouncedSearch]);

  const filteredCourses = useMemo(() => {
    return courses.filter(c => {
      const matchesStatus = statusFilter === 'all' || c.status === statusFilter;
      const matchesSearch = !search || c.title?.toLowerCase().includes(search.toLowerCase());
      return matchesStatus && matchesSearch;
    });
  }, [courses, statusFilter, search]);

 const stats = {
  total: courses.length,  // ← use courses.length, not a separate total variable
  published: courses.filter(c => c.status === 'published').length,
  drafts: courses.filter(c => c.status === 'draft').length,
  totalEnrollments: courses.reduce((s, c) => s + (c.enrollment_count || 0), 0),
};

  const togglePublish = async (course) => {
    const newStatus = course.status === 'published' ? 'draft' : 'published';
    try {
      await api.put(`/courses/${course.id}`, { status: newStatus });
      toast.success(`Course ${newStatus}`);
      fetchCourses();
    } catch { toast.error('Failed to update course'); }
  };

  const duplicateCourse = async (id) => {
    try {
      await api.post(`/courses/${id}/duplicate`);
      toast.success('Course duplicated');
      fetchCourses();
    } catch { toast.error('Failed to duplicate'); }
  };

  const deleteCourse = async (id) => {
    if (!window.confirm('Delete this course and all its content?')) return;
    try {
      await api.delete(`/courses/${id}`);
      toast.success('Course deleted');
      fetchCourses();
    } catch { toast.error('Failed to delete'); }
  };

  const bulkDelete = async () => {
    if (!window.confirm(`Delete ${selectedIds.length} course(s)?`)) return;
    for (const id of selectedIds) {
      try { await api.delete(`/courses/${id}`); } catch {}
    }
    toast.success('Courses deleted');
    setSelectedIds([]);
    fetchCourses();
  };

  const bulkPublish = async (status) => {
    for (const id of selectedIds) {
      try { await api.put(`/courses/${id}`, { status }); } catch {}
    }
    toast.success(`Courses ${status}`);
    setSelectedIds([]);
    fetchCourses();
  };

  const bulkCategory = async (category) => {
    let ok = 0;
    for (const id of selectedIds) {
      try { await api.put(`/courses/${id}`, { category }); ok += 1; } catch {}
    }
    toast.success(`Moved ${ok} course(s) to ${category}`);
    setSelectedIds([]);
    fetchCourses();
  };

  const quickUpdate = async (id, patch, label) => {
    if (patch.title !== undefined && !patch.title) { toast.error('Title cannot be empty'); throw new Error('empty'); }
    if (patch.price !== undefined && (Number.isNaN(patch.price) || patch.price < 0)) { toast.error('Enter a valid price'); throw new Error('bad'); }
    try {
      const res = await api.put(`/courses/${id}`, patch);
      setCourses(prev => prev.map(c => (c.id === id ? { ...c, ...patch, updated_at: res.data?.updated_at || c.updated_at } : c)));
      toast.success(`${label} updated`);
    } catch (e) { toast.error(typeof e.response?.data?.detail === 'string' ? e.response.data.detail : `Could not update ${label.toLowerCase()}`); throw e; }
  };

  const visibleCourses = reviewFilter === 'all' ? courses : courses.filter(c => c.review_status === reviewFilter);

  const toggleSelect = (id) => {
    setSelectedIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  const toggleAll = () => {
    setSelectedIds(prev => prev.length === visibleCourses.length ? [] : visibleCourses.map(c => c.id));
  };

  const getStatusBadge = (s) => {
    if (s === 'published') return <Badge className="bg-green-100 text-green-700 text-[10px]">Published</Badge>;
    if (s === 'archived') return <Badge className="bg-slate-100 text-slate-600 text-[10px]">Archived</Badge>;
    return <Badge className="bg-amber-100 text-amber-700 text-[10px]">Draft</Badge>;
  };

  const getReviewBadge = (c) => {
    if (c.review_status === 'pending') return <Badge className="bg-sky-100 text-sky-700 text-[10px]" data-testid={`review-badge-${c.id}`}>Awaiting approval</Badge>;
    if (c.review_status === 'rejected') return <Badge className="bg-red-100 text-red-700 text-[10px]" data-testid={`review-badge-${c.id}`}>Changes requested</Badge>;
    return null;
  };


  return (
    <DashboardLayout requiredRoles={['super_admin', 'content_manager']}>
      <div className="space-y-6" data-testid="course-management-page">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl lg:text-3xl font-bold text-[#2a3e5d]">Course Management</h1>
            <p className="text-slate-500 mt-1 text-sm">{total} courses total</p>
          </div>
          <Button className="bg-[#20acf6] hover:bg-[#3bb8f7]" onClick={() => navigate(`${base}/new`)} data-testid="new-course-btn">
            <Plus className="h-4 w-4 mr-2" /> Create New Course
          </Button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {[
            { label: 'Total', value: stats.total, color: 'text-[#20acf6]', icon: BookOpen },
            { label: 'Published', value: stats.published, color: 'text-green-600', icon: Eye },
            { label: 'Drafts', value: stats.drafts, color: 'text-amber-600', icon: EyeOff },
            { label: 'Enrollments', value: stats.totalEnrollments, color: 'text-purple-600', icon: Users },
          ].map((s, i) => (
            <Card key={i} className="border-slate-200">
              <CardContent className="p-4 flex items-center gap-3">
                <div className={`p-2 rounded-lg bg-slate-50 ${s.color}`}><s.icon className="h-5 w-5" /></div>
                <div>
                  <p className={`text-xl font-bold ${s.color}`}>{s.value}</p>
                  <p className="text-xs text-slate-500">{s.label}</p>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Filters */}
        <div className="flex flex-wrap gap-3 items-center">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input placeholder="Search by title..." value={search} onChange={e => setSearch(e.target.value)}
              className="pl-10" data-testid="course-search-input" />
          </div>
          <div className="flex rounded-lg border border-slate-200 overflow-hidden" role="tablist" data-testid="course-status-tabs">
            {[['all', 'All'], ['published', 'Published'], ['draft', 'Drafts'], ['archived', 'Archived']].map(([v, l]) => (
              <button key={v} type="button" role="tab" aria-selected={statusFilter === v} onClick={() => setStatusFilter(v)}
                className={`px-3 py-2 text-sm ${statusFilter === v ? 'bg-[#20acf6] text-white' : 'bg-white text-slate-600 hover:bg-slate-50'}`}
                data-testid={`course-tab-${v}`}>
                {l}{statusCounts && v !== 'all' ? ` (${statusCounts[v] ?? 0})` : ''}
              </button>
            ))}
          </div>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-40" data-testid="filter-course-status"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Status</SelectItem>
              <SelectItem value="published">Published</SelectItem>
              <SelectItem value="draft">Drafts</SelectItem>
              <SelectItem value="archived">Archived</SelectItem>
            </SelectContent>
          </Select>
          <Select value={categoryFilter} onValueChange={setCategoryFilter}>
            <SelectTrigger className="w-44" data-testid="filter-course-category"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Categories</SelectItem>
              {CATEGORIES.map(c => <SelectItem key={c.id} value={c.name}>{c.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={reviewFilter} onValueChange={setReviewFilter}>
            <SelectTrigger className="w-48" data-testid="filter-course-review"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Any approval state</SelectItem>
              <SelectItem value="pending">Awaiting approval</SelectItem>
              <SelectItem value="rejected">Changes requested</SelectItem>
            </SelectContent>
          </Select>
          {selectedIds.length > 0 && (
            <div className="flex flex-wrap items-center gap-2" data-testid="course-bulk-bar">
              <span className="text-sm font-medium text-[#2a3e5d]" data-testid="course-selected-count">{selectedIds.length} selected</span>
              {canDelete && <Button size="sm" variant="outline" onClick={() => bulkPublish('published')} data-testid="bulk-publish-btn">Publish Selected</Button>}
              {canDelete && <Button size="sm" variant="outline" onClick={() => bulkPublish('draft')} data-testid="bulk-unpublish-btn">Unpublish Selected</Button>}
              {canDelete && <Button size="sm" variant="outline" onClick={() => bulkPublish('archived')} data-testid="bulk-archive-btn">Archive Selected</Button>}
              <Select value="" onValueChange={bulkCategory}>
                <SelectTrigger className="h-9 w-48" data-testid="bulk-category-select"><SelectValue placeholder="Move to category…" /></SelectTrigger>
                <SelectContent>{CATEGORIES.map(c => <SelectItem key={c.id} value={c.name}>{c.name}</SelectItem>)}</SelectContent>
              </Select>
              {canDelete && <Button size="sm" variant="destructive" onClick={bulkDelete} data-testid="bulk-delete-btn">Delete Selected</Button>}
              <Button size="sm" variant="ghost" onClick={() => setSelectedIds([])} data-testid="bulk-clear-btn">Clear</Button>
            </div>
          )}
        </div>

        {/* Course Table */}
        <Card className="border-slate-200">
          <CardContent className="p-0">
            {loading ? (
              <div className="flex justify-center py-16"><Loader2 className="h-8 w-8 animate-spin text-[#20acf6]" /></div>
            ) : loadError ? (
              <div className="text-center py-14 px-6" data-testid="course-list-error">
                <p className="font-semibold text-red-700">The course list couldn't load ({loadError}).</p>
                <p className="text-sm text-slate-500 mt-1">Your courses are safe in the database — this is a server problem, not missing data.</p>
                <Button size="sm" variant="outline" className="mt-4" onClick={fetchCourses} data-testid="course-list-retry">Try again</Button>
              </div>
            ) : courses.length === 0 ? (
              <div className="text-center py-16 text-slate-500">No courses found. Create your first course above.</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 border-b border-slate-200">
                    <tr>
                      <th className="px-4 py-3 text-left w-10">
                        <input type="checkbox" checked={selectedIds.length === courses.length && courses.length > 0}
                          onChange={toggleAll} className="accent-[#20acf6]" aria-label="Select all courses" data-testid="course-select-all" />
                      </th>
                      <th className="px-4 py-3 text-left font-medium text-slate-600">Course</th>
                      <th className="px-4 py-3 text-left font-medium text-slate-600">Category</th>
                      <th className="px-4 py-3 text-left font-medium text-slate-600">Status</th>
                      <th className="px-4 py-3 text-left font-medium text-slate-600">Price</th>
                      <th className="px-4 py-3 text-left font-medium text-slate-600">Enrollments</th>
                      <th className="px-4 py-3 text-left font-medium text-slate-600">Last Modified</th>
                      <th className="px-4 py-3 text-right font-medium text-slate-600">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredCourses.map(c => (
                      <tr key={c.id || c._id} className="border-b border-slate-100 hover:bg-slate-50/50" data-testid={`course-row-${c.id || c._id}`}>
                        <td className="px-4 py-3">
                          <input type="checkbox" checked={selectedIds.includes(c.id)} onChange={() => toggleSelect(c.id)} className="accent-[#20acf6]" aria-label={`Select ${c.title}`} data-testid={`course-select-${c.id}`} />
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-3">
                            <div className="w-14 h-10 rounded bg-slate-100 overflow-hidden shrink-0 border border-slate-200">
                              {c.thumbnail_url ? (
                                <img 
                                  src={c.thumbnail_url} 
                                  alt="" 
                                  className="w-full h-full object-cover"
                                  onError={(e) => { e.target.style.display = 'none' }}
                                />
                              ) : (
                                <div className="w-full h-full flex items-center justify-center">
                                  <BookOpen className="h-4 w-4 text-slate-300" />
                                </div>
                              )}
                            </div>
                            <div className="min-w-0">
                              <InlineEdit value={c.title} onSave={(v) => quickUpdate(c.id, { title: v }, 'Title')} testId={`inline-title-${c.id}`} className="font-medium text-slate-800 max-w-xs" />
                              <p className="text-xs text-slate-400">{c.module_count} module{c.module_count !== 1 ? 's' : ''} · {c.level}</p>
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-3 text-slate-600 text-xs">{c.category || '—'}</td>
                        <td className="px-4 py-3"><div className="flex flex-wrap gap-1">{getStatusBadge(c.status)}{getReviewBadge(c)}</div></td>
                        <td className="px-4 py-3 text-slate-600">{canDelete ? (
                          <InlineEdit type="number" value={c.price || 0} display={c.price > 0 ? `£${c.price.toFixed(2)}` : <span className="text-green-600 text-xs">Included</span>}
                            onSave={(v) => quickUpdate(c.id, { price: v }, 'Price')} testId={`inline-price-${c.id}`} />
                        ) : (c.price > 0 ? `£${c.price.toFixed(2)}` : <span className="text-green-600 text-xs">Included</span>)}</td>
                        <td className="px-4 py-3 text-slate-600">{c.enrollment_count}</td>
                        <td className="px-4 py-3 text-slate-500 text-xs">{c.updated_at?.slice(0, 10) || c.created_at?.slice(0, 10)}</td>
                        <td className="px-4 py-3 text-right">
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="icon" data-testid={`course-actions-${c.id}`}><MoreHorizontal className="h-4 w-4" /></Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem onClick={() => navigate(`${base}/${c.id}/edit`)} data-testid={`edit-course-${c.id}`}><Edit className="h-4 w-4 mr-2" /> Edit</DropdownMenuItem>
                              <DropdownMenuItem onClick={() => duplicateCourse(c.id)} data-testid={`duplicate-course-${c.id}`}><Copy className="h-4 w-4 mr-2" /> Duplicate</DropdownMenuItem>
                              {canDelete ? (
                                <DropdownMenuItem onClick={() => togglePublish(c)} data-testid={`toggle-publish-course-${c.id}`}>
                                  {c.status === 'published' ? <><EyeOff className="h-4 w-4 mr-2" /> Unpublish</> : <><Eye className="h-4 w-4 mr-2" /> Publish</>}
                                </DropdownMenuItem>
                              ) : (
                                <DropdownMenuItem onClick={() => setSubmitCourseId(c.id)} data-testid={`submit-review-course-${c.id}`}><Send className="h-4 w-4 mr-2" /> Submit for approval</DropdownMenuItem>
                              )}
                              {canDelete && <DropdownMenuItem className="text-red-600" onClick={() => deleteCourse(c.id)} data-testid={`delete-course-${c.id}`}><Trash2 className="h-4 w-4 mr-2" /> Delete</DropdownMenuItem>}
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
      <SubmitReviewDialog open={!!submitCourseId} courseId={submitCourseId} onClose={() => setSubmitCourseId(null)} onSubmitted={() => { setSubmitCourseId(null); fetchCourses(); }} />
    </DashboardLayout>
  );
};

export default CourseManagementPage;
